/**
 * SCB Testing Page
 * Testa SCB API endpoints och visa resultat
 */

import { useState, useEffect } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import { 
  PlayIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  InformationCircleIcon,
  CodeBracketIcon,
  ArrowLeftIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';

interface ScbEndpoint {
  id: string;
  name: string;
  description: string;
  method: 'GET' | 'POST';
  endpoint: string;
  parameters: Array<{
    name: string;
    type: string;
    required: boolean;
    default?: string;
    description: string;
  }>;
  example: any;
}

interface ExecutionResult {
  success: boolean;
  data: any;
  endpoint: string;
  method: string;
  error?: string;
}

const ScbTesting = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [endpoints, setEndpoints] = useState<ScbEndpoint[]>([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState<ScbEndpoint | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ExecutionResult | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [loadingEndpoints, setLoadingEndpoints] = useState(true);

  useEffect(() => {
    loadEndpoints();
  }, []);

  const loadEndpoints = async () => {
    try {
      setLoadingEndpoints(true);
      const response = await api.get<{ success: boolean; data: ScbEndpoint[] }>('/api/crm/scb/endpoints');
      if (response.data.success) {
        setEndpoints(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedEndpoint(response.data.data[0]);
          initializeFormData(response.data.data[0]);
        }
      }
    } catch {
      // Error loading SCB endpoints - handled silently
    } finally {
      setLoadingEndpoints(false);
    }
  };

  const initializeFormData = (endpoint: ScbEndpoint) => {
    const data: Record<string, any> = {};
    
    if (endpoint.example) {
      // Använd exempel-data som utgångspunkt
      Object.assign(data, endpoint.example);
    } else {
      // För GET-endpoints, inga parametrar
      endpoint.parameters.forEach(param => {
        if (param.default) {
          data[param.name] = param.default;
        }
      });
    }
    
    setFormData(data);
  };

  const handleEndpointChange = (endpointId: string) => {
    const endpoint = endpoints.find(e => e.id === endpointId);
    if (endpoint) {
      setSelectedEndpoint(endpoint);
      initializeFormData(endpoint);
      setResult(null);
    }
  };

  const handleFormChange = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleExecute = async () => {
    if (!selectedEndpoint) return;

    try {
      setLoading(true);
      setResult(null);

      const requestBody: any = {
        endpoint: selectedEndpoint.endpoint,
        method: selectedEndpoint.method,
      };

      if (selectedEndpoint.method === 'POST' && Object.keys(formData).length > 0) {
        requestBody.body = formData;
      }

      const response = await api.post<ExecutionResult>('/api/crm/scb/execute', requestBody);
      setResult(response.data);
    } catch (error: any) {
      setResult({
        success: false,
        data: null,
        endpoint: selectedEndpoint.endpoint,
        method: selectedEndpoint.method,
        error: error.response?.data?.error || error.message || 'Okänt fel',
      });
    } finally {
      setLoading(false);
    }
  };

  const renderFormField = (param: ScbEndpoint['parameters'][0]) => {
    const value = formData[param.name] || param.default || '';

    if (param.name === 'variabler' && Array.isArray(value)) {
      // Specialhantering för variabler-array
      return (
        <div key={param.name} className="space-y-2">
          <label className="block text-sm font-medium text-textDefault">
            {param.name} {param.required && <span className="text-red-500">*</span>}
          </label>
          {value.map((varItem: any, index: number) => (
            <div key={index} className="p-3 bg-bgElevated rounded border border-border">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Varde1"
                  value={varItem.Varde1 || ''}
                  onChange={(e) => {
                    const newVariabler = [...value];
                    newVariabler[index] = { ...varItem, Varde1: e.target.value };
                    handleFormChange('variabler', newVariabler);
                  }}
                  className="px-3 py-2 bg-bgContainer border border-border rounded text-textDefault"
                />
                <input
                  type="text"
                  placeholder="Variabel"
                  value={varItem.Variabel || ''}
                  onChange={(e) => {
                    const newVariabler = [...value];
                    newVariabler[index] = { ...varItem, Variabel: e.target.value };
                    handleFormChange('variabler', newVariabler);
                  }}
                  className="px-3 py-2 bg-bgContainer border border-border rounded text-textDefault"
                />
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div key={param.name}>
        <label className="block text-sm font-medium text-textDefault mb-1">
          {param.name} {param.required && <span className="text-red-500">*</span>}
          {param.description && (
            <span className="text-xs text-textSecondary ml-2">({param.description})</span>
          )}
        </label>
        <input
          type="text"
          value={value}
          onChange={(e) => handleFormChange(param.name, e.target.value)}
          placeholder={param.default || ''}
          className="w-full px-3 py-2 bg-bgContainer border border-border rounded text-textDefault"
        />
      </div>
    );
  };

  return (
    <div className="p-6 bg-bgContainer rounded-lg h-full">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate(ROUTES.CRM)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
            <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
          </button>
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <BeakerIcon className="w-6 h-6 text-primary" />
            {t('scbTesting.title') || 'SCB API Testning'}
          </h1>
        </div>
        <p className="text-textSecondary">
          {t('scbTesting.description') || 'Testa SCB API endpoints och visa resultat'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vänster: Endpoint-val och formulär */}
        <div className="space-y-4">
          <div className="bg-bgElevated rounded-lg border border-border p-4">
            <h2 className="text-lg font-semibold text-textDefault mb-4">
              {t('scbTesting.selectEndpoint') || 'Välj Endpoint'}
            </h2>
            
            {loadingEndpoints ? (
              <div className="text-textSecondary">Laddar endpoints...</div>
            ) : (
              <select
                value={selectedEndpoint?.id || ''}
                onChange={(e) => handleEndpointChange(e.target.value)}
                className="w-full px-3 py-2 bg-bgContainer border border-border rounded text-textDefault mb-4"
              >
                {endpoints.map(endpoint => (
                  <option key={endpoint.id} value={endpoint.id}>
                    {endpoint.method} - {endpoint.name}
                  </option>
                ))}
              </select>
            )}

            {selectedEndpoint && (
              <>
                <div className="mb-4">
                  <p className="text-sm text-textSecondary mb-2">
                    {selectedEndpoint.description}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-textSecondary">
                    <CodeBracketIcon className="w-4 h-4" />
                    <code className="bg-bgContainer px-2 py-1 rounded">
                      {selectedEndpoint.method} {selectedEndpoint.endpoint}
                    </code>
                  </div>
                </div>

                {selectedEndpoint.method === 'POST' && selectedEndpoint.parameters.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-textDefault">
                      {t('scbTesting.parameters') || 'Parametrar'}
                    </h3>
                    {selectedEndpoint.parameters.map(param => renderFormField(param))}
                  </div>
                )}

                <button
                  onClick={handleExecute}
                  disabled={loading}
                  className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-bgPrimary text-white rounded hover:bg-bgPrimaryHover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlayIcon className="w-5 h-5" />
                  {loading 
                    ? (t('scbTesting.executing') || 'Kör...') 
                    : (t('scbTesting.execute') || 'Kör Endpoint')
                  }
                </button>
              </>
            )}
          </div>
        </div>

        {/* Höger: Resultat */}
        <div className="space-y-4">
          <div className="bg-bgElevated rounded-lg border border-border p-4">
            <h2 className="text-lg font-semibold text-textDefault mb-4">
              {t('scbTesting.result') || 'Resultat'}
            </h2>

            {result ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  {result.success ? (
                    <>
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                      <span className="text-green-500 font-medium">
                        {t('scbTesting.success') || 'Framgångsrikt'}
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="w-5 h-5 text-red-500" />
                      <span className="text-red-500 font-medium">
                        {t('scbTesting.error') || 'Fel'}
                      </span>
                    </>
                  )}
                </div>

                {result.error && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                    <p className="text-sm text-red-700 dark:text-red-400">{result.error}</p>
                  </div>
                )}

                {result.data && (
                  <div>
                    <h3 className="text-sm font-medium text-textDefault mb-2">
                      {t('scbTesting.responseData') || 'Response Data'}
                    </h3>
                    <pre className="p-4 bg-bgContainer border border-border rounded text-xs overflow-auto max-h-96 text-textDefault">
                      {JSON.stringify(result.data, null, 2)}
                    </pre>
                  </div>
                )}

                <div className="text-xs text-textSecondary">
                  <div>
                    <strong>Endpoint:</strong> {result.endpoint}
                  </div>
                  <div>
                    <strong>Method:</strong> {result.method}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-textSecondary">
                <InformationCircleIcon className="w-5 h-5" />
                <span>
                  {t('scbTesting.noResult') || 'Inget resultat än. Välj en endpoint och klicka på "Kör Endpoint".'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScbTesting;
